/**
 * SelectPecaBusca — combobox buscável de tipo de peça/manifestação.
 *
 * Mesmo catálogo do dropdown do card (tiposPeca.ts: TIPOS_PECA/GRUPOS), mas com
 * filtro real ao digitar (não só "pular pra opção" do <select> nativo) — usa o
 * `normalizar()` já existente (acento/caixa-insensível).
 */
import { useEffect, useRef, useState } from 'react';
import { GRUPOS, TIPOS_PECA, normalizar, type TipoPeca } from '../tiposPeca';

interface Props {
  value: TipoPeca | null;
  onChange: (t: TipoPeca) => void;
  className?: string;
}

export function SelectPecaBusca({ value, onChange, className }: Props) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setAberto(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const termo = normalizar(busca.trim());
  const filtrados = termo
    ? TIPOS_PECA.filter((t) => normalizar(t.rotulo).includes(termo))
    : TIPOS_PECA;

  const inputClasse =
    className ??
    'w-full text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={aberto ? busca : (value?.rotulo ?? '')}
        onChange={(e) => { setBusca(e.target.value); setAberto(true); }}
        onFocus={() => { setBusca(''); setAberto(true); }}
        placeholder="Digite para buscar…"
        className={inputClasse}
      />
      {aberto && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-auto rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg text-sm">
          {filtrados.length === 0 && (
            <div className="px-3 py-2 text-gray-400 dark:text-gray-500">nenhuma peça encontrada</div>
          )}
          {GRUPOS.map((g) => {
            const itens = filtrados.filter((t) => t.grupo === g);
            if (itens.length === 0) return null;
            return (
              <div key={g}>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wide font-bold text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900/40">
                  {g}
                </div>
                {itens.map((t) => (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => { onChange(t); setBusca(''); setAberto(false); }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${
                      value?.key === t.key ? 'bg-blue-50 dark:bg-blue-900/20 font-medium' : ''
                    }`}
                  >
                    {t.rotulo}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
